'use client';

import { SESSION_STATUS } from '@/constants/auth';
import { APOLLO_FETCH_POLICY } from '@/constants/apollo';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from 'next-auth/react';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import { useStartSearchOnMount } from '../useStartSearchOnMount';
import { useGetAnimalEbvsLazyQuery } from '@/publicSearch/graphql/queries.generated';
import { useMemberAssociation } from '@/publicSearch/graphql/memberAssociation/hooks';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import { SEARCH_TYPE } from '@/constants/searchType';
import useGridStore from '@/publicSearch/store/useGridStore';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { useIsEbvEnabled } from '@/hooks/publicSearch/useIsEbvEnabled';
import { useAnimalDisplayType } from '@/hooks/publicSearch/animal/useAnimalDisplayType';
import { RESULT_DISPLAY_TYPE } from '@/constants/resultDisplay';
import {
  FieldOption,
  GetAnimalsRow,
} from '@/components/publicSearch/constants';
import type {
  AnimalEbvFilteredSortOptionInput,
  AnimalEbvFilterField,
} from '@/graphqlClient/types';
import { isFilterInputIncompleteForQuery } from '@/components/publicSearch/search/searchFilterEbvExpansion';
import { useRouter } from 'next/navigation';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import { useCurrencySymbol } from '@/hooks/publicSearch/useCurrencySymbol';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import { useEbvTraitExplanations } from '@/components/publicSearch/animal/ebv/useEbvTraitExplanations';
import {
  generateEbvColumnGroupingModel,
  ebvGroupingOptionsFromDisplay,
  parseEbvAverage,
  parseEbvConfig,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import {
  buildAnimalEbvColumns,
  buildAnimalEbvRowsWithAverageLabel,
  buildAnimalEbvSortOptions,
  parseAnimalSearchFieldsConfig,
} from './animalEbvGrid';
import {
  buildAnimalFilterGroups,
  buildAnimalFilterGroupsFromGroups,
} from './buildAnimalFilterGroups';
import { isUnmappedI6LookupError } from '@/components/publicSearch/search/expandI6LookupFilters';

export function useAnimalEbvFilter(enabled: boolean) {
  const router = useRouter();
  const { status } = useSession();
  const isAuthenticated = status === SESSION_STATUS.AUTHENTICATED;
  const orgCode = usePublicSearchContext(
    (context) => context.society.societyCode!,
  );
  const orgId = usePublicSearchContext((context) => context.society.orgId);
  const society = usePublicSearchContext((context) => context.society);
  const isEbvEnabled = useIsEbvEnabled();

  const { data: memberAssocData } = useMemberAssociation(!isAuthenticated);
  const myAnimalsMemberIds = useMemo(
    () => memberAssocData?.memberAssociation ?? [],
    [memberAssocData?.memberAssociation],
  );

  // Cache total count to avoid expensive COUNT queries on page changes.
  // `cachedTotalCountRef` mirrors the state so `startSearch` can read the
  // current value WITHOUT taking `cachedTotalCount` as a dependency. Keeping
  // the state in `startSearch`'s deps created a feedback loop: each search
  // response cached the count → state changed → `startSearch` identity changed
  // → `useStartSearchOnMount` re-fired the search. The state is still used by
  // `effectiveTotalCount` for rendering.
  const [cachedTotalCount, setCachedTotalCount] = useState<number | null>(null);
  const cachedTotalCountRef = useRef<number | null>(null);
  const previousFilterSignature = useRef<string | null>(null);

  const [getAnimalEbvs, { data, error, loading, previousData, refetch }] =
    useGetAnimalEbvsLazyQuery({
      fetchPolicy: APOLLO_FETCH_POLICY.CACHE_FIRST,
      nextFetchPolicy: APOLLO_FETCH_POLICY.CACHE_FIRST,
    });
  // After a graphql error or aborted request, the underlying ObservableQuery
  // can stay in a "completed without data" state where calling getAnimalEbvs
  // again with the same variables doesn't re-issue a network request — only
  // apolloClient.resetStore() (today wired up only on society switch) recovers
  // it. To recover in-page, route the *next* startSearch call through
  // refetch(), which uses a disposable observable + network-only. We only do
  // this when we know the prior attempt errored — on the happy path we keep
  // the lazy execute / cache-first behavior so successful responses don't
  // trigger a second network round-trip.
  const recoveredFromErrorRef = useRef<unknown>(null);

  // See useSaleCatalogueFilter — Apollo's lazy-query handles can swap
  // reference between the render that fires the first execute and the
  // render that commits the response. Read them through refs so startSearch
  // is stable across the post-fetch render.
  const getAnimalEbvsRef = useRef<typeof getAnimalEbvs>(getAnimalEbvs);
  const refetchRef = useRef<typeof refetch>(refetch);
  const errorRef = useRef<typeof error>(error);
  // Keep latest-value refs in sync after each commit (read by the stable callback below).
  useEffect(() => {
    getAnimalEbvsRef.current = getAnimalEbvs;
    refetchRef.current = refetch;
    errorRef.current = error;
  });

  const {
    commonFilterOperator,
    filters,
    filterGroups: storeFilterGroups,
    groupsOperator,
  } = useFilterStore(SEARCH_TYPE.ANIMAL);
  const {
    myAnimals,
    animalsOnSaleOnly,
    gridSortModel,
    paginationModel,
    showEBVs,
    showEBVAccuracy,
    showPercentiles,
  } = useGridStore();
  const { resultDisplay } = useAnimalDisplayType();
  // Hide EBV average row in card view (gallery)
  const isCardView = resultDisplay === RESULT_DISPLAY_TYPE.GALLERY;

  // Parse EBV configuration from society settings
  const ebvConfig = useMemo(
    () => parseEbvConfig(society.settings),
    [society.settings],
  );
  const effectiveEbvConfig = useMemo(
    () => (isEbvEnabled ? ebvConfig : []),
    [isEbvEnabled, ebvConfig],
  );

  // Parse EBV Average from society settings
  const ebvAverage = useMemo(
    () => parseEbvAverage(society.settings),
    [society.settings],
  );

  // Parse ANIMAL_LIST_FIELDS from society settings for column config
  const animalSearchFieldsConfig = useMemo(
    () => parseAnimalSearchFieldsConfig(society.settings),
    [society.settings],
  );

  const currencySymbol = useCurrencySymbol(society);
  const {
    ebvLabel,
    showAccuracy,
    showBreedAverages,
    showPercentileSearch,
    showBreedplanHelp,
    isBlackRedAngus,
    useColourSlotGrouping,
  } = useEbvDisplayOptions();
  const effectiveShowEBVAccuracy = showEBVAccuracy && showAccuracy;
  const effectiveShowPercentiles = showPercentiles && showPercentileSearch;
  const effectiveEbvAverage = showBreedAverages ? ebvAverage : null;
  const { traitHeaderContext, popoverElement: ebvTraitExplanationPopover } =
    useEbvTraitExplanations(society.settings, showBreedplanHelp);

  const useGrouped = storeFilterGroups.length > 0;

  const filterGroups = useMemo(() => {
    try {
      return useGrouped
        ? buildAnimalFilterGroupsFromGroups(
            storeFilterGroups,
            groupsOperator,
            myAnimals,
            myAnimalsMemberIds,
            orgId,
            animalsOnSaleOnly,
            society.settings,
          )
        : buildAnimalFilterGroups(
            filters,
            commonFilterOperator,
            myAnimals,
            myAnimalsMemberIds,
            orgId,
            animalsOnSaleOnly,
            society.settings,
          );
    } catch (error) {
      // ILRX-4434: do not search with a silently dropped Select-if (wrong counts).
      if (isUnmappedI6LookupError(error)) {
        return null;
      }
      throw error;
    }
  }, [
    useGrouped,
    storeFilterGroups,
    groupsOperator,
    commonFilterOperator,
    filters,
    myAnimals,
    animalsOnSaleOnly,
    myAnimalsMemberIds,
    orgId,
    society.settings,
  ]);

  const sortOptions = useMemo<AnimalEbvFilteredSortOptionInput[]>(
    () => buildAnimalEbvSortOptions(gridSortModel),
    [gridSortModel],
  );

  const columns = useMemo(
    () =>
      buildAnimalEbvColumns({
        orgCode,
        ebvConfig: effectiveEbvConfig,
        animalSearchFieldsConfig,
        showEBVs,
        showEBVAccuracy: effectiveShowEBVAccuracy,
        showPercentiles: effectiveShowPercentiles,
        currencySymbol,
        ebvLabel,
        onAverageRowClick:
          isEbvEnabled && showPercentileSearch && effectiveEbvConfig.length > 0
            ? () =>
                router.push(
                  `/${orgCode}/${ROUTE_SEGMENT.ANIMALS}/ebv-percentages`,
                )
            : undefined,
        traitHeaderContext,
        isBlackRedAngusStrain: isBlackRedAngus,
        ebvAverage: effectiveEbvAverage,
        showPercentileSearch,
        societyId: orgId,
      }),
    [
      orgCode,
      effectiveEbvConfig,
      animalSearchFieldsConfig,
      showEBVs,
      effectiveShowEBVAccuracy,
      effectiveShowPercentiles,
      currencySymbol,
      ebvLabel,
      router,
      isEbvEnabled,
      showPercentileSearch,
      traitHeaderContext,
      isBlackRedAngus,
      effectiveEbvAverage,
      orgId,
    ],
  );

  const columnGroupingModel = useMemo(() => {
    if (effectiveEbvConfig.length === 0) return [];
    return generateEbvColumnGroupingModel(
      effectiveEbvConfig,
      ebvGroupingOptionsFromDisplay({ useColourSlotGrouping }),
    );
  }, [effectiveEbvConfig, useColourSlotGrouping]);

  // Render rows from `data ?? previousData` rather than clearing them on
  // every `loading` flip — the explicit `if (loading) return []` was the
  // visible "data being rerendered" jank: every refetch (filter / sort /
  // pagination / EBV toggle) blanked the grid for one render before the
  // new payload arrived. MUI's `loading` overlay already covers the
  // grid; emptying the rows underneath it just produces a flash.
  const rows = useMemo(() => {
    return buildAnimalEbvRowsWithAverageLabel({
      items: (data ?? previousData)?.animalEbvs?.items,
      ebvConfig: effectiveEbvConfig,
      ebvAverage: effectiveEbvAverage,
      showEBVs,
      showEBVAccuracy: effectiveShowEBVAccuracy,
      showPercentiles: effectiveShowPercentiles,
      isCardView,
      currencySymbol,
      isBlackRedAngus,
      showPercentileSearch,
    });
  }, [
    data,
    previousData,
    effectiveEbvConfig,
    effectiveEbvAverage,
    isCardView,
    showEBVs,
    effectiveShowEBVAccuracy,
    effectiveShowPercentiles,
    currencySymbol,
    isBlackRedAngus,
    showPercentileSearch,
  ]);

  // Create a signature of filter/sort state to detect changes
  const filterSignature = useMemo(() => {
    return JSON.stringify({ filterGroups, sortOptions, orgId });
  }, [filterGroups, sortOptions, orgId]);

  // Determine if filters are simple enough for approximate count
  // Approximate count works best for unfiltered or lightly filtered queries
  // Backend will use sys.partitions statistics for very fast row count estimates
  const canUseApproximateCount = useMemo(() => {
    // Approximate count only works for unfiltered queries (just SocietyId)
    // Complex filters require exact COUNT(*) which the backend will handle

    // Check if we only have the SocietyId filter (which is always present)
    const allFilters = filterGroups?.[0]?.groups?.[0]?.filters || [];

    // SocietyId is always the first filter, so if length is 1, we only have SocietyId
    // This means no additional user filters = perfect for approximate count

    return (
      allFilters.length === 1 &&
      allFilters[0]?.field ===
        (FieldOption.societyId.value as AnimalEbvFilterField)
    );
  }, [filterGroups]);

  const startSearch = useCallback(() => {
    if (!enabled) return;
    // NOTE: do not gate the entire animal list query on isEbvEnabled. EBV
    // status only affects whether the EBV columns are rendered (handled via
    // effectiveEbvConfig); for societies whose data has isEbvEnabled=false
    // we still need to fire GetAnimalEbvs to populate the basic animal grid
    // — otherwise hard-refreshing /<society>/animals leaves the page stuck
    // with no animal POST in the network tab.

    // Skip if orgId is missing or filterGroups is null
    if (!orgId || !filterGroups) return;

    // When using grouped filters, skip flat filter validation
    if (
      !useGrouped &&
      filters.length > 0 &&
      filters.some((f) => isFilterInputIncompleteForQuery(f))
    ) {
      return;
    }

    // Determine if we need to request total count
    // Only request count when filters/sort change, not on page changes.
    // Read the cached count from the ref so this callback doesn't depend on
    // the state (see cachedTotalCountRef declaration).
    const filtersChanged = filterSignature !== previousFilterSignature.current;
    const includeTotalCount =
      filtersChanged || cachedTotalCountRef.current === null;

    if (filtersChanged) {
      // Reset cached count when filters change
      cachedTotalCountRef.current = null;
      setCachedTotalCount(null);
      previousFilterSignature.current = filterSignature;
    }

    const variables = {
      requestInput: {
        pagination: {
          currentPage: (paginationModel.page ?? 0) + 1,
          pageSize: paginationModel.pageSize,
        },
        filterGroups,
        sortOptions,
        // Performance optimization: skip COUNT query on page changes
        includeTotalCount,
        // Use approximate count for simple filters (much faster for large tables)
        useApproximateCount: includeTotalCount && canUseApproximateCount,
      },
    };
    const context = { headers: setDefaultHeader(orgId) };

    // Recovery path: there's a tracked error and we haven't yet retried for
    // *this* error instance. Use refetch() to force a fresh disposable
    // network request, which gets the ObservableQuery unstuck.
    const currentError = errorRef.current;
    if (currentError && recoveredFromErrorRef.current !== currentError) {
      recoveredFromErrorRef.current = currentError;
      try {
        void refetchRef.current(variables);
      } catch {
        // refetch() throws synchronously if the lazy query hasn't received
        // a first emission yet; fall back to the lazy execute, which is
        // safe to call from any state.
        getAnimalEbvsRef.current({ variables, context });
      }
      return;
    }
    getAnimalEbvsRef.current({ variables, context });
    // `cachedTotalCount` is intentionally NOT a dependency — it is read via
    // `cachedTotalCountRef` so that caching a count after a response does not
    // recreate this callback and re-fire the search.
  }, [
    enabled,
    filterGroups,
    filterSignature,
    filters,
    orgId,
    paginationModel.page,
    paginationModel.pageSize,
    sortOptions,
    canUseApproximateCount,
    useGrouped,
  ]);

  // Content signature of the actual request variables, used by
  // useStartSearchOnMount to de-dupe redundant fires (StrictMode +
  // stale-then-fresh society.settings churn). Distinct from `filterSignature`
  // above: this MUST include pagination so page changes still re-fire, whereas
  // `filterSignature` deliberately excludes it (page changes skip the COUNT).
  const searchSignature = useMemo(
    () =>
      JSON.stringify({
        filterGroups,
        sortOptions,
        orgId,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      }),
    [
      filterGroups,
      sortOptions,
      orgId,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );

  useStartSearchOnMount(startSearch, enabled, searchSignature);

  // Cache total count when received from server (skip if -1 which means count was not requested)
  useEffect(() => {
    if (
      data?.animalEbvs?.totalCount !== undefined &&
      data.animalEbvs.totalCount >= 0
    ) {
      // Keep the ref in sync so startSearch reads the latest cached count.
      cachedTotalCountRef.current = data.animalEbvs.totalCount;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- caches the total count from the async Apollo query response (also set in startSearch); this mirrors async fetched data into state, not a render-time derivation
      setCachedTotalCount(data.animalEbvs.totalCount);
    }
  }, [data?.animalEbvs?.totalCount]);

  // Use cached total count when server returns -1 (count was skipped for performance)
  const effectiveTotalCount = useMemo(() => {
    const serverCount = data?.animalEbvs?.totalCount;
    if (serverCount !== undefined && serverCount >= 0) {
      return serverCount;
    }
    return cachedTotalCount ?? 0;
  }, [data?.animalEbvs?.totalCount, cachedTotalCount]);

  return useMemo(
    () => ({
      data: data?.animalEbvs
        ? { ...data.animalEbvs, totalCount: effectiveTotalCount }
        : undefined,
      dataGrid: {
        columns,
        rows,
        columnGroupingModel,
        getRowId: (row: GetAnimalsRow) => row.ID,
      },
      done: (data || error) && !loading,
      error,
      loading,
      previousData: previousData?.animalEbvs,
      startSearch,
      ebvTraitExplanationPopover,
    }),
    [
      columnGroupingModel,
      columns,
      data,
      effectiveTotalCount,
      error,
      loading,
      previousData,
      rows,
      startSearch,
      ebvTraitExplanationPopover,
    ],
  );
}
